import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Crosshair, MagnifyingGlass, UserPlus, Briefcase, Users, Target,
  MapPin, CurrencyGbp, Star, Lightning, CaretRight, Phone,
  EnvelopeSimple, LinkedinLogo, CheckCircle, XCircle, WarningCircle,
  ArrowRight, Funnel, Eye, GithubLogo, Globe, Database, ArrowDown,
  Sparkle, Clock, Link as LinkIcon
} from "@phosphor-icons/react";
import { toast } from "sonner";
import CallButton from "@/components/CallButton";
import Pagination from "@/components/Pagination";

const DISCOVERY_STATUSES = [
  "new_discovery", "reviewing", "qualified", "contacted", "engaged",
  "submitted", "shortlisted", "progressed", "placed",
  "rejected", "withdrawn", "duplicate",
];

const CANDIDATE_STATUSES = [
  "sourced", "qualified", "contacted", "engaged", "submitted",
  "shortlisted", "interview", "progressed", "placed", "rejected", "withdrawn",
];

const STATUS_COLORS = {
  new_discovery: "bg-indigo-50 text-indigo-700 border-indigo-300",
  reviewing: "bg-blue-50 text-blue-700 border-blue-300",
  sourced: "bg-slate-100 text-slate-700 border-slate-300",
  qualified: "bg-blue-50 text-blue-700 border-blue-300",
  contacted: "bg-indigo-50 text-indigo-700 border-indigo-300",
  engaged: "bg-purple-50 text-purple-700 border-purple-300",
  submitted: "bg-amber-50 text-amber-700 border-amber-300",
  shortlisted: "bg-orange-50 text-orange-700 border-orange-300",
  interview: "bg-cyan-50 text-cyan-700 border-cyan-300",
  progressed: "bg-teal-50 text-teal-700 border-teal-300",
  placed: "bg-emerald-50 text-emerald-700 border-emerald-300",
  rejected: "bg-red-50 text-red-700 border-red-300",
  withdrawn: "bg-gray-100 text-gray-600 border-gray-300",
  duplicate: "bg-gray-100 text-gray-500 border-gray-300",
};

const SECTORS = [
  "Healthcare & Social Care", "IT & Technology", "Accounting & Finance",
  "Education & Childcare", "Commercial Sales & Business Development",
];

function MatchBadge({ score }) {
  if (!score && score !== 0) return null;
  const color = score >= 70 ? "bg-emerald-50 text-emerald-700 border-emerald-300" :
    score >= 40 ? "bg-amber-50 text-amber-700 border-amber-300" :
      "bg-red-50 text-red-700 border-red-300";
  return <Badge variant="outline" className={`text-[10px] font-bold ${color}`}>{score}% match</Badge>;
}

function SourceIcon({ source }) {
  if (source === "GitHub") return <GithubLogo size={14} className="text-[#333]" />;
  if (source === "StackOverflow") return <Database size={14} className="text-orange-500" />;
  return <Globe size={14} className="text-[#6B7280]" />;
}

/* ═══════════════════════════════════════════════════════ */
/* DISCOVERY FEED — External candidate discoveries        */
/* ═══════════════════════════════════════════════════════ */
function DiscoveryFeed({ vacancyId }) {
  const [discoveries, setDiscoveries] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [discovering, setDiscovering] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: String(page), limit: "25" });
      if (vacancyId && vacancyId !== "__all__") params.set("vacancy_id", vacancyId);
      if (statusFilter && statusFilter !== "__all__") params.set("status", statusFilter);
      const { data } = await api.get(`/candidate-engine/discoveries?${params}`);
      setDiscoveries(data.discoveries || []);
      setTotalPages(data.pages || 1);
    } catch { toast.error("Failed to load discoveries"); }
    setLoading(false);
  }, [page, vacancyId, statusFilter]);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await api.get("/candidate-engine/discovery-stats");
      setStats(data);
    } catch {}
  }, []);

  useEffect(() => { load(); loadStats(); }, [load, loadStats]);

  const runDiscovery = async () => {
    setDiscovering(true);
    try {
      const body = {};
      if (vacancyId && vacancyId !== "__all__") body.vacancy_id = vacancyId;
      const { data } = await api.post("/candidate-engine/discover", body);
      toast.success(data.message || "Discovery complete");
      load();
      loadStats();
    } catch (err) { toast.error(err?.response?.data?.detail || "Discovery failed"); }
    setDiscovering(false);
  };

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/candidate-engine/discoveries/${id}/status`, { status });
      toast.success(`Updated to ${status}`);
      setDiscoveries(prev => prev.map(d => d.id === id ? { ...d, discovery_status: status } : d));
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const importToHub = async (id) => {
    try {
      const { data } = await api.post(`/candidate-engine/discoveries/${id}/import`);
      if (data.imported) {
        toast.success("Candidate imported to Hub");
        setDiscoveries(prev => prev.map(d => d.id === id ? { ...d, discovery_status: "qualified", imported_candidate_id: data.candidate?.id } : d));
      } else {
        toast.info(data.message || "Already linked");
      }
    } catch (err) { toast.error(err?.response?.data?.detail || "Import failed"); }
  };

  return (
    <div className="space-y-4" data-testid="discovery-feed">
      {/* Discovery stats */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-6 text-xs">
          <div><span className="text-[#6B7280]">Total: </span><span className="font-bold text-[#0F172A]">{stats.total || 0}</span></div>
          <div><span className="text-[#6B7280]">New: </span><span className="font-bold text-indigo-700">{stats.new || 0}</span></div>
          <div><span className="text-[#6B7280]">Today: </span><span className="font-bold text-emerald-700">{stats.today || 0}</span></div>
          <div><span className="text-[#6B7280]">Reviewing: </span><span className="font-bold text-blue-700">{stats.reviewing || 0}</span></div>
        </div>
        <div className="flex-1" />
        <Select value={statusFilter || "__all__"} onValueChange={v => { setStatusFilter(v === "__all__" ? "" : v); setPage(1); }}>
          <SelectTrigger className="h-8 w-[150px] text-xs"><SelectValue placeholder="All statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All statuses</SelectItem>
            <SelectItem value="new_discovery">New Discovery</SelectItem>
            <SelectItem value="reviewing">Reviewing</SelectItem>
            <SelectItem value="qualified">Qualified</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={runDiscovery} disabled={discovering} className="gap-1.5 bg-[#0F172A] hover:bg-[#1E293B]" data-testid="discover-now-btn">
          <MagnifyingGlass size={14} /> {discovering ? "Discovering..." : "Find Candidates Now"}
        </Button>
      </div>

      {stats.last_scan && (
        <div className="text-[10px] text-[#9CA3AF] flex items-center gap-1">
          <Clock size={10} /> Last scan: {stats.last_scan.completed_at?.split("T")[0] || "—"} — {stats.last_scan.total_discovered || 0} discovered from {stats.last_scan.sources_used?.join(", ") || "no sources"}
          {stats.last_scan.status === "failed" && <Badge variant="outline" className="text-[9px] border-red-300 text-red-600 ml-1">Failed</Badge>}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12"><div className="w-6 h-6 border-2 border-[#0F172A] border-t-transparent rounded-full animate-spin" /></div>
      ) : discoveries.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-lg border border-[#E5E7EB]">
          <Sparkle size={40} className="text-[#D1D5DB] mx-auto mb-3" />
          <div className="text-sm text-[#6B7280]">No new suitable candidates discovered.</div>
          <div className="text-xs text-[#9CA3AF] mt-1 max-w-md mx-auto">Click "Find Candidates Now" to search public professional profiles matching your active vacancies. Discovery uses GitHub, StackOverflow, and other public sources.</div>
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {discoveries.map(d => (
              <div key={d.id} className="bg-white rounded-lg border border-[#E5E7EB] p-4 hover:shadow-sm transition-shadow" data-testid={`discovery-${d.id}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <SourceIcon source={d.source} />
                    <div>
                      <div className="text-sm font-semibold text-[#0F172A]">{d.name}</div>
                      <div className="text-xs text-[#6B7280]">{d.current_title || "—"}{d.current_employer ? ` at ${d.current_employer}` : ""}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <MatchBadge score={d.match_score} />
                    <Badge variant="outline" className={`text-[10px] capitalize ${STATUS_COLORS[d.discovery_status] || ""}`}>
                      {d.discovery_status === "new_discovery" ? "NEW DISCOVERY" : d.discovery_status?.replace(/_/g, " ")}
                    </Badge>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 text-xs text-[#6B7280] mb-2">
                  {d.location && <div className="flex items-center gap-1"><MapPin size={11} /> {d.location}</div>}
                  {d.salary && <div className="flex items-center gap-1"><CurrencyGbp size={11} /> {d.salary}</div>}
                  {d.vacancy_title && <div className="flex items-center gap-1"><Briefcase size={11} /> {d.vacancy_title}</div>}
                  {d.company_name && <div className="flex items-center gap-1"><Target size={11} /> {d.company_name}</div>}
                  {d.experience_summary && <div className="flex items-center gap-1"><Star size={11} /> {d.experience_summary}</div>}
                  {d.discovered_at && <div className="flex items-center gap-1"><Clock size={11} /> {d.discovered_at.split("T")[0]}</div>}
                </div>

                {d.skills?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-2">
                    {d.skills.slice(0, 6).map((s, i) => <span key={i} className="text-[10px] bg-[#F3F4F6] text-[#374151] px-1.5 py-0.5 rounded">{s}</span>)}
                  </div>
                )}

                {d.match_reasons?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-2">
                    {d.match_reasons.map((r, i) => <span key={i} className="text-[10px] bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded flex items-center gap-0.5"><CheckCircle size={9} /> {r}</span>)}
                  </div>
                )}

                <div className="flex items-center gap-2 pt-2 border-t border-[#F3F4F6]">
                  {d.source_url && (
                    <a href={d.source_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1" data-testid={`source-link-${d.id}`}>
                      <LinkIcon size={12} /> View Source
                    </a>
                  )}
                  {d.email && (
                    <a href={`mailto:${d.email}`} className="text-xs text-[#6B7280] hover:text-[#0F172A] flex items-center gap-1">
                      <EnvelopeSimple size={12} /> {d.email}
                    </a>
                  )}
                  <div className="flex-1" />
                  {d.discovery_status === "new_discovery" && (
                    <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1" onClick={() => updateStatus(d.id, "reviewing")} data-testid={`review-${d.id}`}>
                      <Eye size={12} /> Review
                    </Button>
                  )}
                  {(d.discovery_status === "new_discovery" || d.discovery_status === "reviewing") && (
                    <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1 text-red-600 border-red-200 hover:bg-red-50" onClick={() => updateStatus(d.id, "rejected")}>
                      <XCircle size={12} /> Reject
                    </Button>
                  )}
                  {(d.discovery_status === "reviewing" || d.discovery_status === "new_discovery") && !d.imported_candidate_id && (
                    <Button size="sm" className="h-7 text-[10px] gap-1 bg-[#0F172A] hover:bg-[#1E293B]" onClick={() => importToHub(d.id)} data-testid={`import-${d.id}`}>
                      <ArrowDown size={12} /> Import to Hub
                    </Button>
                  )}
                  {d.imported_candidate_id && (
                    <Badge className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-300">Imported</Badge>
                  )}
                  {d.existing_candidate_id && d.discovery_status === "duplicate" && (
                    <Badge className="text-[10px] bg-amber-50 text-amber-700 border-amber-300">Existing Candidate</Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
          {totalPages > 1 && <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />}
        </>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════ */
/* INTERNAL HUB SEARCH                                    */
/* ═══════════════════════════════════════════════════════ */
function InternalSearch({ selectedVacancy }) {
  const [searchForm, setSearchForm] = useState({ keywords: "", location: "", sector: "", status: "" });
  const [results, setResults] = useState([]);
  const [searchTotal, setSearchTotal] = useState(0);
  const [searching, setSearching] = useState(false);

  const doSearch = async () => {
    setSearching(true);
    try {
      const payload = {};
      if (searchForm.keywords) payload.keywords = searchForm.keywords;
      if (searchForm.location) payload.location = searchForm.location;
      if (searchForm.sector && searchForm.sector !== "__any__") payload.sector = searchForm.sector;
      if (searchForm.status && searchForm.status !== "__any__") payload.status = searchForm.status;
      if (selectedVacancy && selectedVacancy !== "__all__") payload.vacancy_id = selectedVacancy;
      const { data } = await api.post("/candidate-engine/search", payload);
      setResults(data.results || []);
      setSearchTotal(data.total || 0);
    } catch { toast.error("Search failed"); }
    setSearching(false);
  };

  const updateStatus = async (candidateId, newStatus) => {
    try {
      await api.put(`/candidate-engine/status/${candidateId}`, { status: newStatus });
      toast.success(`Status updated to ${newStatus}`);
      setResults(prev => prev.map(c => c.id === candidateId ? { ...c, candidate_status: newStatus } : c));
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const matchCandidate = async (candidateId) => {
    if (!selectedVacancy || selectedVacancy === "__all__") { toast.error("Select a vacancy first"); return; }
    try {
      const { data } = await api.post("/candidate-engine/match", { candidate_id: candidateId, vacancy_id: selectedVacancy });
      toast.success(`Match score: ${data.match?.score}%`);
      setResults(prev => prev.map(c => c.id === candidateId ? { ...c, match: data.match } : c));
    } catch (err) { toast.error(err?.response?.data?.detail || "Match failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg border border-[#E5E7EB] p-4">
        <div className="flex items-end gap-3 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs font-medium text-[#374151]">Keywords</label>
            <Input value={searchForm.keywords} onChange={e => setSearchForm(f => ({ ...f, keywords: e.target.value }))} placeholder="Name, title, skills..." className="h-9 mt-1" data-testid="search-keywords" onKeyDown={e => e.key === "Enter" && doSearch()} />
          </div>
          <div className="w-[140px]">
            <label className="text-xs font-medium text-[#374151]">Location</label>
            <Input value={searchForm.location} onChange={e => setSearchForm(f => ({ ...f, location: e.target.value }))} placeholder="e.g. London" className="h-9 mt-1" />
          </div>
          <div className="w-[170px]">
            <label className="text-xs font-medium text-[#374151]">Sector</label>
            <Select value={searchForm.sector || "__any__"} onValueChange={v => setSearchForm(f => ({ ...f, sector: v }))}>
              <SelectTrigger className="h-9 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__any__">Any sector</SelectItem>
                {SECTORS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={doSearch} disabled={searching} className="gap-1.5 bg-[#0F172A] hover:bg-[#1E293B]" data-testid="search-btn">
            <MagnifyingGlass size={14} /> {searching ? "Searching..." : "Search Hub"}
          </Button>
        </div>
      </div>

      {results.length > 0 && <div className="text-xs text-[#6B7280]">{searchTotal} candidates in Hub{selectedVacancy && selectedVacancy !== "__all__" ? " — sorted by match score" : ""}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {results.map(c => {
          const match = c.match;
          return (
            <div key={c.id} className="bg-white rounded-lg border border-[#E5E7EB] p-4 hover:shadow-sm transition-shadow" data-testid={`candidate-card-${c.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="text-sm font-semibold text-[#0F172A]">{c.first_name} {c.last_name}</div>
                  <div className="text-xs text-[#6B7280]">{c.current_title || "No title"}</div>
                </div>
                <div className="flex items-center gap-1.5">
                  {match && <MatchBadge score={match.score} />}
                  <Badge variant="outline" className={`text-[10px] ${STATUS_COLORS[c.candidate_status] || STATUS_COLORS.sourced}`}>
                    {c.candidate_status || "sourced"}
                  </Badge>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-[#6B7280] mb-2">
                {c.location && <div className="flex items-center gap-1"><MapPin size={11} /> {c.location}</div>}
                {c.sector && <div className="flex items-center gap-1"><Briefcase size={11} /> {c.sector}</div>}
              </div>
              {c.skills?.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2">
                  {c.skills.slice(0, 5).map((s, i) => <span key={i} className="text-[10px] bg-[#F3F4F6] text-[#374151] px-1.5 py-0.5 rounded">{s}</span>)}
                </div>
              )}
              {match?.reasons?.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2">
                  {match.reasons.map((r, i) => <span key={i} className="text-[10px] bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded flex items-center gap-0.5"><CheckCircle size={9} /> {r}</span>)}
                </div>
              )}
              <div className="flex items-center gap-2 pt-2 border-t border-[#F3F4F6]">
                {c.phone && <CallButton phone={c.phone} name={`${c.first_name} ${c.last_name}`} size="xs" />}
                {c.email && <a href={`mailto:${c.email}`} className="text-xs text-[#6B7280] hover:text-[#0F172A] flex items-center gap-1"><EnvelopeSimple size={12} /> Email</a>}
                <div className="flex-1" />
                <Select value={c.candidate_status || "sourced"} onValueChange={v => updateStatus(c.id, v)}>
                  <SelectTrigger className="h-7 w-[110px] text-[10px]" data-testid={`status-select-${c.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{CANDIDATE_STATUSES.map(s => <SelectItem key={s} value={s} className="text-xs capitalize">{s}</SelectItem>)}</SelectContent>
                </Select>
                {selectedVacancy && selectedVacancy !== "__all__" && !match && (
                  <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1" onClick={() => matchCandidate(c.id)} data-testid={`match-btn-${c.id}`}>
                    <Target size={12} /> Match
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {!searching && results.length === 0 && (
        <div className="text-center py-12 bg-white rounded-lg border border-[#E5E7EB]">
          <MagnifyingGlass size={36} className="text-[#D1D5DB] mx-auto mb-3" />
          <div className="text-sm text-[#9CA3AF]">Search existing Hub candidates by keyword, location, or sector</div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════ */
/* PIPELINE VIEW                                          */
/* ═══════════════════════════════════════════════════════ */
function PipelineView({ vacancyId }) {
  const [pipeline, setPipeline] = useState({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = vacancyId && vacancyId !== "__all__" ? `?vacancy_id=${vacancyId}` : "";
      const { data } = await api.get(`/candidate-engine/pipeline${params}`);
      setPipeline(data.pipeline || {});
      setTotal(data.total || 0);
    } catch { toast.error("Failed to load pipeline"); }
    setLoading(false);
  }, [vacancyId]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (candidateId, newStatus) => {
    try {
      await api.put(`/candidate-engine/status/${candidateId}`, { status: newStatus });
      toast.success(`Status updated to ${newStatus}`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  if (loading) return <div className="flex items-center justify-center py-12"><div className="w-6 h-6 border-2 border-[#0F172A] border-t-transparent rounded-full animate-spin" /></div>;

  const activeStatuses = CANDIDATE_STATUSES.filter(s => (pipeline[s]?.length || 0) > 0);
  if (activeStatuses.length === 0) return (
    <div className="text-center py-12 bg-white rounded-lg border border-[#E5E7EB]">
      <Funnel size={36} className="text-[#D1D5DB] mx-auto mb-3" />
      <div className="text-sm text-[#9CA3AF]">No candidates in pipeline{vacancyId && vacancyId !== "__all__" ? " for this vacancy" : ""}</div>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="pipeline-view">
      <div className="text-xs text-[#6B7280]">{total} candidates{vacancyId && vacancyId !== "__all__" ? " (filtered by vacancy)" : ""}</div>
      <div className="flex gap-3 overflow-x-auto pb-2">
        {activeStatuses.map(status => (
          <div key={status} className="min-w-[240px] max-w-[260px] flex-shrink-0">
            <div className="flex items-center gap-2 mb-2">
              <Badge variant="outline" className={`text-[10px] capitalize ${STATUS_COLORS[status]}`}>{status}</Badge>
              <span className="text-xs text-[#9CA3AF]">{pipeline[status]?.length || 0}</span>
            </div>
            <div className="space-y-2">
              {(pipeline[status] || []).map(c => (
                <div key={c.id} className="bg-white rounded-lg border border-[#E5E7EB] p-3 text-xs" data-testid={`pipeline-card-${c.id}`}>
                  <div className="font-medium text-[#0F172A] mb-1">{c.first_name} {c.last_name}</div>
                  <div className="text-[#6B7280] mb-1.5">{c.current_title || "No title"}</div>
                  {c.vacancy_id && <div className="text-[10px] text-[#9CA3AF] mb-1.5 flex items-center gap-0.5"><Briefcase size={9} /> Linked to vacancy</div>}
                  <Select value={status} onValueChange={v => updateStatus(c.id, v)}>
                    <SelectTrigger className="h-6 text-[10px]"><SelectValue /></SelectTrigger>
                    <SelectContent>{CANDIDATE_STATUSES.map(s => <SelectItem key={s} value={s} className="text-xs capitalize">{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════ */
/* CV-LIBRARY BUTTON                                      */
/* ═══════════════════════════════════════════════════════ */
function CvLibraryButton({ isAdmin }) {
  const [showDialog, setShowDialog] = useState(false);
  const [sourceStatus, setSourceStatus] = useState(null);

  useEffect(() => {
    api.get("/candidate-engine/sources").then(({ data }) => {
      setSourceStatus(data.sources?.cv_library || null);
    }).catch(() => {});
  }, []);

  const connected = sourceStatus?.connected;

  return (
    <>
      <Button variant="outline" onClick={() => setShowDialog(true)} className="gap-1.5 border-purple-300 text-purple-700 hover:bg-purple-50" data-testid="cv-library-btn">
        <Database size={14} /> CV-LIBRARY
        {connected && <div className="w-2 h-2 rounded-full bg-emerald-500 ml-1" />}
        {!connected && <div className="w-2 h-2 rounded-full bg-gray-400 ml-1" />}
      </Button>
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md" data-testid="cv-library-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Database size={20} className="text-purple-600" /> CV-Library Integration
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {connected ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-sm text-emerald-700">
                <div className="flex items-center gap-2 font-semibold mb-1"><CheckCircle size={16} /> Connected</div>
                <p className="text-xs">CV-Library integration is active. Candidate searches will include CV-Library results.</p>
              </div>
            ) : (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-[#6B7280]">
                <div className="flex items-center gap-2 font-semibold text-[#374151] mb-2"><WarningCircle size={16} className="text-amber-500" /> CV-Library integration not yet connected.</div>
                <p className="text-xs mb-3">When API credentials are provided, this integration will enable direct CV/candidate search from CV-Library's authorised database.</p>
                {isAdmin && (
                  <div className="bg-white border border-[#E5E7EB] rounded-md p-3">
                    <div className="text-xs font-medium text-[#374151] mb-1">Configure Integration</div>
                    <p className="text-[10px] text-[#9CA3AF] mb-2">Contact your CV-Library account manager to obtain API access credentials, then configure them via Hub Settings or environment variables.</p>
                    <p className="text-[10px] text-[#9CA3AF]">Required: CV_LIBRARY_API_KEY environment variable</p>
                  </div>
                )}
                {!isAdmin && (
                  <p className="text-xs text-[#9CA3AF]">Please contact your Team Leader or Admin to set up this integration.</p>
                )}
              </div>
            )}
            <div className="text-[10px] text-[#9CA3AF]">
              Credentials are stored securely on the server. Candidate Finders cannot view or modify API keys.
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* ═══════════════════════════════════════════════════════ */
/* ADD CANDIDATE MODAL                                    */
/* ═══════════════════════════════════════════════════════ */
function AddCandidateModal({ open, onClose, onAdded, vacancyId }) {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", current_title: "", location: "", salary_expectation: "", sector: "", skills: "", experience_years: 0, notes: "", linkedin_url: "", source: "Manual Entry", vacancy_id: vacancyId || "" });
  const [saving, setSaving] = useState(false);
  const [dupWarning, setDupWarning] = useState(null);

  useEffect(() => { setForm(f => ({ ...f, vacancy_id: vacancyId || "" })); }, [vacancyId]);

  const submit = async () => {
    if (!form.first_name || !form.last_name) { toast.error("Name is required"); return; }
    setSaving(true);
    setDupWarning(null);
    try {
      const payload = { ...form, skills: form.skills ? form.skills.split(",").map(s => s.trim()).filter(Boolean) : [], experience_years: parseInt(form.experience_years) || 0 };
      const { data } = await api.post("/candidate-engine/add-candidate", payload);
      if (data.duplicate) {
        setDupWarning(data.existing);
        toast.warning("Potential duplicate found");
      } else {
        toast.success("Candidate added");
        onAdded(data.candidate);
        onClose();
        setForm({ first_name: "", last_name: "", email: "", phone: "", current_title: "", location: "", salary_expectation: "", sector: "", skills: "", experience_years: 0, notes: "", linkedin_url: "", source: "Manual Entry", vacancy_id: vacancyId || "" });
      }
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="add-candidate-modal">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><UserPlus size={20} className="text-[#C5A059]" /> Add Candidate</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {dupWarning && (
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-xs">
              <div className="flex items-center gap-1 text-amber-700 font-semibold mb-1"><WarningCircle size={14} /> Potential Existing Candidate</div>
              <div className="text-amber-600">{dupWarning.first_name} {dupWarning.last_name} — {dupWarning.email || dupWarning.phone || "No contact"}</div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-medium text-[#374151]">First Name *</label><Input value={form.first_name} onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} className="h-9 mt-1" data-testid="candidate-first-name" /></div>
            <div><label className="text-xs font-medium text-[#374151]">Last Name *</label><Input value={form.last_name} onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} className="h-9 mt-1" data-testid="candidate-last-name" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-medium text-[#374151]">Email</label><Input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="h-9 mt-1" /></div>
            <div><label className="text-xs font-medium text-[#374151]">Phone</label><Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="h-9 mt-1" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-medium text-[#374151]">Job Title</label><Input value={form.current_title} onChange={e => setForm(f => ({ ...f, current_title: e.target.value }))} className="h-9 mt-1" /></div>
            <div><label className="text-xs font-medium text-[#374151]">Location</label><Input value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} className="h-9 mt-1" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-medium text-[#374151]">Salary Expectation</label><Input value={form.salary_expectation} onChange={e => setForm(f => ({ ...f, salary_expectation: e.target.value }))} className="h-9 mt-1" placeholder="e.g. 35000" /></div>
            <div><label className="text-xs font-medium text-[#374151]">Experience (years)</label><Input type="number" value={form.experience_years} onChange={e => setForm(f => ({ ...f, experience_years: e.target.value }))} className="h-9 mt-1" /></div>
          </div>
          <div>
            <label className="text-xs font-medium text-[#374151]">Sector</label>
            <Select value={form.sector || "__none__"} onValueChange={v => setForm(f => ({ ...f, sector: v === "__none__" ? "" : v }))}>
              <SelectTrigger className="h-9 mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent><SelectItem value="__none__">—</SelectItem>{SECTORS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><label className="text-xs font-medium text-[#374151]">Skills (comma-separated)</label><Input value={form.skills} onChange={e => setForm(f => ({ ...f, skills: e.target.value }))} className="h-9 mt-1" placeholder="e.g. Python, SQL, AWS" /></div>
          <div><label className="text-xs font-medium text-[#374151]">LinkedIn URL</label><Input value={form.linkedin_url} onChange={e => setForm(f => ({ ...f, linkedin_url: e.target.value }))} className="h-9 mt-1" /></div>
          <div><label className="text-xs font-medium text-[#374151]">Notes</label><textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="w-full mt-1 rounded-md border border-[#E5E7EB] px-3 py-2 text-sm min-h-[60px] resize-none" /></div>
          <Button onClick={submit} disabled={saving} className="w-full" data-testid="submit-add-candidate">{saving ? "Adding..." : "Add Candidate"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ═══════════════════════════════════════════════════════ */
/* MAIN PAGE                                              */
/* ═══════════════════════════════════════════════════════ */
export default function CandidateEnginePage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "team_leader";
  const [tab, setTab] = useState("discoveries");
  const [vacancies, setVacancies] = useState([]);
  const [selectedVacancy, setSelectedVacancy] = useState("__all__");
  const [showAdd, setShowAdd] = useState(false);

  useEffect(() => {
    api.get("/candidate-engine/vacancies").then(({ data }) => setVacancies(data)).catch(() => {});
  }, []);

  const selectedVacObj = vacancies.find(v => v.id === selectedVacancy);

  return (
    <div data-testid="candidate-engine-page" className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-[#0F172A] flex items-center gap-2">
            <Crosshair size={24} weight="duotone" className="text-[#C5A059]" />
            Candidate Talent Engine
          </h1>
          <p className="text-sm text-[#6B7280]">Discover, source, match, and manage candidates for live vacancies</p>
        </div>
        <div className="flex items-center gap-2">
          <CvLibraryButton isAdmin={isAdmin} />
          <Button onClick={() => setShowAdd(true)} className="gap-1.5 bg-[#0F172A] hover:bg-[#1E293B]" data-testid="add-candidate-btn">
            <UserPlus size={16} /> Add Candidate
          </Button>
        </div>
      </div>

      {/* Vacancy Selector */}
      <div className="bg-white rounded-lg border border-[#E5E7EB] p-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-sm font-medium text-[#374151]">
            <Briefcase size={16} className="text-[#C5A059]" /> Target Vacancy:
          </div>
          <Select value={selectedVacancy} onValueChange={setSelectedVacancy}>
            <SelectTrigger className="h-9 w-[320px]" data-testid="vacancy-selector">
              <SelectValue placeholder="All vacancies" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All vacancies</SelectItem>
              {vacancies.map(v => (
                <SelectItem key={v.id} value={v.id}>
                  {v.title} — {v.company_name || "No company"} {v.location ? `(${v.location})` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedVacObj && (
            <div className="flex items-center gap-2 text-xs text-[#6B7280]">
              {selectedVacObj.location && <span className="flex items-center gap-0.5"><MapPin size={11} /> {selectedVacObj.location}</span>}
              {selectedVacObj.salary && <span className="flex items-center gap-0.5"><CurrencyGbp size={11} /> {selectedVacObj.salary}</span>}
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[#F3F4F6]" data-testid="engine-tabs">
          <TabsTrigger value="discoveries" className="gap-1"><Sparkle size={14} /> Discoveries</TabsTrigger>
          <TabsTrigger value="search" className="gap-1"><MagnifyingGlass size={14} /> Hub Search</TabsTrigger>
          <TabsTrigger value="pipeline" className="gap-1"><Funnel size={14} /> Pipeline</TabsTrigger>
        </TabsList>

        <TabsContent value="discoveries" className="mt-4">
          <DiscoveryFeed vacancyId={selectedVacancy} />
        </TabsContent>

        <TabsContent value="search" className="mt-4">
          <InternalSearch selectedVacancy={selectedVacancy} />
        </TabsContent>

        <TabsContent value="pipeline" className="mt-4">
          <PipelineView vacancyId={selectedVacancy} />
        </TabsContent>
      </Tabs>

      <AddCandidateModal open={showAdd} onClose={() => setShowAdd(false)} onAdded={() => {}} vacancyId={selectedVacancy && selectedVacancy !== "__all__" ? selectedVacancy : ""} />
    </div>
  );
}
